// Files tab — drives the mock connection's thread-file state. Inject host-side files,
// transition status across uploaded/processing/ready/failed (with reason chooser),
// trigger fileCleaned, toggle force-reject on detach. Capabilities controls live up
// top so the dev can exercise the unsupported / fileTooLarge rejection paths.

import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './simulatorFilesTab.html?raw';
import lang from 'dojo/_base/lang';
import _WidgetBaseSP from 'more/_WidgetBaseSP';

import { MockEvisio2Sdk } from "../../mockSdk/mockEvisio2Sdk";
import { MockEvisio2Connection, MockStateChannel, MockThreadFile } from "../../mockSdk/mockEvisio2Connection";

class simulatorFilesTab extends compose(_WidgetBaseSP) {
    templateString: any = template;

    public sdk: MockEvisio2Sdk;
    public connection: MockEvisio2Connection;

    private capFilesCheckbox:          HTMLInputElement;
    private capMaxUploadBytesInput:    HTMLInputElement;
    private capMaxUploadBytesClearBtn: HTMLButtonElement;

    private injectNameInput:      HTMLInputElement;
    private injectMimeInput:      HTMLInputElement;
    private injectStatusSelect:   HTMLSelectElement;
    private injectDirectionSelect:HTMLSelectElement;
    private injectBytesFile:      HTMLInputElement;
    private injectBtn:            HTMLButtonElement;
    private injectStatusMsg:      HTMLSpanElement;

    private filesTableContainer:  HTMLDivElement;

    private _stateChangeDisposer: (() => void) | null = null;

    constructor(args?: { sdk: MockEvisio2Sdk; connection: MockEvisio2Connection }){
        super(...(arguments as unknown as []));
        if (args){ this.sdk = args.sdk; this.connection = args.connection; }
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.capFilesCheckbox.addEventListener("change", lang.hitch(this, this._onCapFilesChange));
        this.capMaxUploadBytesInput.addEventListener("change", lang.hitch(this, this._onCapMaxBytesChange));
        this.capMaxUploadBytesClearBtn.addEventListener("click", lang.hitch(this, this._onCapMaxBytesClear));
        this.injectBtn.addEventListener("click", lang.hitch(this, this._onClickInject));

        this._renderCapabilities();
        this._renderFilesTable();

        this._stateChangeDisposer = this.connection.addStateChangeListener(lang.hitch(this, this._onStateChange));
    }

    startup(): void { super.startup.apply(this, arguments as any); }

    destroy(): void {
        if (this._stateChangeDisposer){ try { this._stateChangeDisposer(); } catch { /* ignore */ } this._stateChangeDisposer = null; }
        super.destroy.apply(this, arguments as any);
    }

    private _onStateChange(channel: MockStateChannel): void {
        if (channel === "files") this._renderFilesTable();
        if (channel === "capabilities") this._renderCapabilities();
    }

    private _renderCapabilities(): void {
        let snap = this.connection._snapshot();
        this.capFilesCheckbox.checked = !!snap.capabilities.files;
        this.capMaxUploadBytesInput.value = (snap.capabilities.maxUploadBytes != null) ? String(snap.capabilities.maxUploadBytes) : "";
    }

    private _onCapFilesChange(): void {
        this.connection._setCapabilities({ files: this.capFilesCheckbox.checked });
    }
    private _onCapMaxBytesChange(): void {
        let raw = this.capMaxUploadBytesInput.value;
        if (raw === ""){ this.connection._setCapabilities({ maxUploadBytes: null }); return; }
        let n = parseInt(raw, 10);
        if (!isFinite(n) || n < 0){ this.capMaxUploadBytesInput.value = ""; this.connection._setCapabilities({ maxUploadBytes: null }); return; }
        this.connection._setCapabilities({ maxUploadBytes: n });
    }
    private _onCapMaxBytesClear(): void {
        this.capMaxUploadBytesInput.value = "";
        this.connection._setCapabilities({ maxUploadBytes: null });
    }

    private async _onClickInject(): Promise<void> {
        let name = this.injectNameInput.value || "untitled.bin";
        let mime = this.injectMimeInput.value || "application/octet-stream";
        let status = this.injectStatusSelect.value as ("uploaded" | "processing" | "ready" | "failed");
        let direction = this.injectDirectionSelect.value as ("input" | "output" | "toolResult");

        let bytes: ArrayBuffer;
        let file = this.injectBytesFile.files && this.injectBytesFile.files[0];
        if (file){
            bytes = await file.arrayBuffer();
            if (!this.injectMimeInput.value && file.type){ mime = file.type; }
            if (!this.injectNameInput.value && file.name){ name = file.name; }
        } else {
            // No file picked — use a tiny placeholder so the bytes field is non-empty.
            let blob = new Blob([new Uint8Array([0x4d, 0x4f, 0x43, 0x4b])]); // "MOCK"
            bytes = await blob.arrayBuffer();
        }

        let id = this.connection._injectFile({
            name: name,
            mime: mime,
            bytes: bytes,
            direction: direction,
            status: status,
            available: true,
        });
        this._setInjectStatus("injected " + id, false);
    }

    private _setInjectStatus(msg: string, isError: boolean): void {
        this.injectStatusMsg.textContent = msg;
        this.injectStatusMsg.style.color = isError ? "#ff8c8c" : "#9bd07b";
    }

    private _renderFilesTable(): void {
        let files = this.connection._getAllFiles();
        this.filesTableContainer.innerHTML = "";

        if (files.length === 0){
            let empty = document.createElement("div");
            empty.style.cssText = "opacity:0.5; padding:6px 4px;";
            empty.textContent = "(no thread files)";
            this.filesTableContainer.appendChild(empty);
            return;
        }

        for (let i = 0; i < files.length; i++){
            this.filesTableContainer.appendChild(this._renderFileCard(files[i]));
        }
    }

    // Each thread file renders as a vertically-stacked card — narrow-slot-friendly. Header
    // row has the id + status badge; body shows name/mime/direction/availability; footer
    // has the transition/clean/detach/remove controls.
    private _renderFileCard(f: MockThreadFile): HTMLElement {
        let card = document.createElement("div");
        card.style.cssText = "display:flex; flex-direction:column; gap:4px; padding:6px 8px; background:#16171a; border:1px solid #2a2b30; border-radius:3px; font-family:'Segoe UI',sans-serif; font-size:11px;";

        // Header: id + status badge.
        let header = document.createElement("div");
        header.style.cssText = "display:flex; gap:6px; align-items:center; justify-content:space-between;";
        let idEl = document.createElement("div");
        idEl.textContent = f.id;
        idEl.style.cssText = "font-family:Consolas,monospace; opacity:0.7;";
        let statusBadge = document.createElement("span");
        statusBadge.textContent = f.status;
        statusBadge.style.cssText = "padding:1px 6px; border-radius:2px; background:" + this._statusColor(f.status) + "; color:white; font-size:10px;";
        header.appendChild(idEl);
        header.appendChild(statusBadge);

        // Body: name (+direction), mime, fail reason, availability.
        let body = document.createElement("div");
        body.style.cssText = "display:flex; flex-direction:column; gap:1px;";
        let nameRow = document.createElement("div");
        nameRow.textContent = f.name + "  (" + f.direction + ")";
        nameRow.style.cssText = "font-weight:600;";
        let mimeRow = document.createElement("div");
        mimeRow.textContent = f.mime;
        mimeRow.style.cssText = "opacity:0.6; font-family:Consolas,monospace;";
        body.appendChild(nameRow);
        body.appendChild(mimeRow);
        if (f.status === "failed" && f.failReason){
            let reasonRow = document.createElement("div");
            reasonRow.textContent = "reason: " + f.failReason;
            reasonRow.style.cssText = "color:#ff8c8c; font-family:Consolas,monospace;";
            body.appendChild(reasonRow);
        }
        if (!f.available){
            let cleanedRow = document.createElement("div");
            cleanedRow.textContent = "cleaned (fetchArtifact will reject)";
            cleanedRow.style.cssText = "color:#ff8c8c;";
            body.appendChild(cleanedRow);
        }

        // Footer: transition + clean + detach + remove.
        let footer = document.createElement("div");
        footer.style.cssText = "display:flex; gap:4px; flex-wrap:wrap; margin-top:2px;";

        let transitionSelect = document.createElement("select");
        transitionSelect.style.cssText = "background:#1f2024; color:#e6e6e6; border:1px solid #2a2b30; border-radius:3px; padding:2px 4px; font-family:inherit; font-size:10px;";
        let optionsList = ["uploaded", "processing", "ready", "failed:too_many_pages:73:50", "failed:too_many_slides:31:30", "failed:too_long", "failed:conversion_unavailable", "failed:custom"];
        for (let i = 0; i < optionsList.length; i++){
            let opt = document.createElement("option"); opt.value = optionsList[i]; opt.textContent = optionsList[i]; transitionSelect.appendChild(opt);
        }
        transitionSelect.value = f.status;
        let transitionBtn = document.createElement("button");
        transitionBtn.type = "button";
        transitionBtn.textContent = "→";
        transitionBtn.title = "Transition to chosen status";
        transitionBtn.style.cssText = "background:transparent; color:#cfd0d4; border:1px solid #3a3b41; border-radius:3px; padding:2px 6px; cursor:pointer; font-family:inherit; font-size:10px;";
        transitionBtn.addEventListener("click", lang.hitch(this, () => {
            let v = transitionSelect.value;
            if (v.indexOf("failed:") === 0){
                let reason = v.substring("failed:".length);
                if (reason === "custom"){
                    let custom = window.prompt("Custom failure reason (free-form English; show to user as-is):", "");
                    if (custom == null) return;
                    reason = custom;
                }
                this.connection._transitionFileStatus(f.id, "failed", reason);
            } else {
                this.connection._transitionFileStatus(f.id, v as any);
            }
        }));

        let cleanBtn = document.createElement("button");
        cleanBtn.type = "button";
        cleanBtn.textContent = "fileCleaned";
        cleanBtn.style.cssText = "background:transparent; color:#cfd0d4; border:1px solid #3a3b41; border-radius:3px; padding:2px 6px; cursor:pointer; font-family:inherit; font-size:10px;";
        cleanBtn.addEventListener("click", lang.hitch(this, () => { this.connection._markFileCleaned(f.id); }));

        let detachBtn = document.createElement("button");
        detachBtn.type = "button";
        detachBtn.textContent = "reject-detach: " + (f.forceRejectDetach ? "ON" : "OFF");
        detachBtn.title = "Toggle force-reject on the next detachFile call (tests the detachFailed path)";
        detachBtn.style.cssText = "background:transparent; color:#cfd0d4; border:1px solid #3a3b41; border-radius:3px; padding:2px 6px; cursor:pointer; font-family:inherit; font-size:10px;";
        detachBtn.addEventListener("click", lang.hitch(this, () => { this.connection._setFileForceRejectDetach(f.id, !f.forceRejectDetach); }));

        let removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.textContent = "remove";
        removeBtn.style.cssText = "background:transparent; color:#ff8c8c; border:1px solid #5a3a3a; border-radius:3px; padding:2px 6px; cursor:pointer; font-family:inherit; font-size:10px;";
        removeBtn.addEventListener("click", lang.hitch(this, () => { this.connection._removeFile(f.id); }));

        footer.appendChild(transitionSelect);
        footer.appendChild(transitionBtn);
        footer.appendChild(cleanBtn);
        footer.appendChild(detachBtn);
        footer.appendChild(removeBtn);

        card.appendChild(header);
        card.appendChild(body);
        card.appendChild(footer);
        return card;
    }

    private _statusColor(status: string): string {
        switch (status){
            case "ready":      return "#2d8a3e";
            case "processing": return "#3a7afe";
            case "uploaded":   return "#6c757d";
            case "failed":     return "#c0392b";
            default:           return "#5a5b61";
        }
    }
}

export default mdsWidget(simulatorFilesTab);
